import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// no withCredentials – Bearer token in Authorization header is set globally by AuthContext

// Returns origin including the active locale prefix so Stripe redirects back
// directly to /{locale}/checkout/success without an intermediate redirect that
// might unmount the success page before its polling loop fires.
function localeOriginUrl() {
  const m = (typeof window !== "undefined" && window.location.pathname || "").match(/^\/([a-z]{2})(?:\/|$)/);
  const locale = m ? m[1] : "de";
  return `${window.location.origin}/${locale}`;
}

export const api = {
  // listings
  listListings: (params) => axios.get(`${API}/listings`, { params }).then(r => r.data),
  getListing: (id) => axios.get(`${API}/listings/${id}`).then(r => r.data),
  getSimilarListings: (id, limit = 6) => axios.get(`${API}/listings/${id}/similar`, { params: { limit } }).then(r => r.data),
  createListing: (data) => axios.post(`${API}/listings`, data).then(r => r.data),
  updateListing: (id, data) => axios.put(`${API}/listings/${id}`, data).then(r => r.data),
  deleteListing: (id) => axios.delete(`${API}/listings/${id}`).then(r => r.data),
  myListings: () => axios.get(`${API}/listings/mine/all`).then(r => r.data),
  // Phase 72 — batch existence check for Recently-Viewed auto-cleanup
  listingsExist: (ids) => axios.post(`${API}/listings/exist`, { ids }).then(r => r.data),
  // upload
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  fileUrl: (path) => `${API}/files/${path}`,
  // favorites
  listFavorites: () => axios.get(`${API}/favorites`).then(r => r.data),
  addFavorite: (id) => axios.post(`${API}/favorites/${id}`).then(r => r.data),
  removeFavorite: (id) => axios.delete(`${API}/favorites/${id}`).then(r => r.data),
  // messages
  sendMessage: (data) => axios.post(`${API}/messages`, data).then(r => r.data),
  threads: () => axios.get(`${API}/messages/threads`).then(r => r.data),
  thread: (lid, uid) => axios.get(`${API}/messages/thread/${lid}/${uid}`).then(r => r.data),
  // packages / subscriptions
  packages: () => axios.get(`${API}/packages`).then(r => r.data),
  mySubscription: () => axios.get(`${API}/subscriptions/me`).then(r => r.data),
  activateFree: () => axios.post(`${API}/subscriptions/free`).then(r => r.data),
  createCheckout: (package_id) => axios.post(`${API}/checkout/session`, { package_id, origin_url: localeOriginUrl() }).then(r => r.data),
  checkoutStatus: (session_id) => axios.get(`${API}/checkout/status/${session_id}`).then(r => r.data),
  // misc
  stats: () => axios.get(`${API}/stats`).then(r => r.data),
  // saved searches
  listSearches: () => axios.get(`${API}/searches`).then(r => r.data),
  createSearch: (data) => axios.post(`${API}/searches`, data).then(r => r.data),
  patchSearch: (id, patch) => axios.patch(`${API}/searches/${id}`, patch).then(r => r.data),
  deleteSearch: (id) => axios.delete(`${API}/searches/${id}`).then(r => r.data),
  // ai
  describeListing: (data) => axios.post(`${API}/ai/describe-listing`, data).then(r => r.data),
  suggestTitle: (data) => axios.post(`${API}/ai/suggest-title`, data).then(r => r.data),
  // price suggestion
  priceSuggestion: (params) => axios.get(`${API}/price-suggestion`, { params }).then(r => r.data),
  // boost / featured
  boostListing: (id, days = 7) => axios.post(`${API}/listings/${id}/boost`, { days }).then(r => r.data),
  cancelBoost: (id) => axios.delete(`${API}/listings/${id}/boost`).then(r => r.data),
  featuredListings: (limit = 6) => axios.get(`${API}/listings-featured`, { params: { limit } }).then(r => r.data),
  // boost via Stripe (Phase 56: supports boost-7d / boost-14d via package_id)
  createBoostCheckout: (listingId, packageId = "boost-7d") => axios.post(`${API}/boost/checkout-session`, { listing_id: listingId, origin_url: localeOriginUrl(), package_id: packageId }).then(r => r.data),
  // Phase 56 — Premium listing (CHF 19.90 / 30d) via Stripe + TWINT
  createPremiumCheckout: (listingId) => axios.post(`${API}/premium/checkout-session`, { listing_id: listingId, origin_url: localeOriginUrl() }).then(r => r.data),
  // Phase 56 — Recurring dealer subscription (card only)
  createDealerSubscription: (packageId) => axios.post(`${API}/dealer/subscribe`, { package_id: packageId, origin_url: localeOriginUrl() }).then(r => r.data),
  myDealerSubscription: () => axios.get(`${API}/dealer/subscription/me`).then(r => r.data),
  // Phase 57 — Stripe Billing Portal link (self-service: card / invoices / cancel / upgrade)
  dealerPortalLink: (returnUrl) => axios.post(`${API}/dealer/portal-link`, { return_url: returnUrl }).then(r => r.data),
  // password reset
  forgotPassword: (email, locale) => axios.post(`${API}/auth/forgot-password`, { email, locale }).then(r => r.data),
  resetPassword: (token, password) => axios.post(`${API}/auth/reset-password`, { token, password }).then(r => r.data),
  // email change
  requestEmailChange: (new_email, current_password, locale) => axios.post(`${API}/auth/request-email-change`, { new_email, current_password, locale }).then(r => r.data),
  verifyEmailChange: (token) => axios.post(`${API}/auth/verify-email-change`, { token }).then(r => r.data),
  // admin
  adminStats: () => axios.get(`${API}/admin/stats`).then(r => r.data),
  adminUsers: (params) => axios.get(`${API}/admin/users`, { params }).then(r => r.data),
  adminSuspendUser: (id, reason) => axios.post(`${API}/admin/users/${id}/suspend`, { reason }).then(r => r.data),
  adminUnsuspendUser: (id) => axios.post(`${API}/admin/users/${id}/unsuspend`).then(r => r.data),
  adminListings: (params) => axios.get(`${API}/admin/listings`, { params }).then(r => r.data),
  adminHideListing: (id, reason) => axios.post(`${API}/admin/listings/${id}/hide`, { reason }).then(r => r.data),
  adminUnhideListing: (id) => axios.post(`${API}/admin/listings/${id}/unhide`).then(r => r.data),
  adminDeleteListing: (id, reason) => axios.delete(`${API}/admin/listings/${id}`, { data: { reason } }).then(r => r.data),
  adminSavedSearches: (params) => axios.get(`${API}/admin/saved-searches`, { params }).then(r => r.data),
  adminMessages: (params) => axios.get(`${API}/admin/messages`, { params }).then(r => r.data),
  adminAuditLog: (params) => axios.get(`${API}/admin/audit-log`, { params }).then(r => r.data),
  // dealer (public)
  listDealers: (params) => axios.get(`${API}/dealers`, { params }).then(r => r.data),
  getDealer: (slug) => axios.get(`${API}/dealers/${encodeURIComponent(slug)}`).then(r => r.data),
  // Phase 66 — guest inquiry (no account required)
  sendGuestInquiry: (listingId, payload) => axios.post(`${API}/listings/${encodeURIComponent(listingId)}/guest-inquiry`, payload).then(r => r.data),
  // Phase 62 — trending listings
  trendingListings: (params) => axios.get(`${API}/trending/listings`, { params }).then(r => r.data),
  // Phase 61 — listing activity / social proof
  listingActivity: (id) => axios.get(`${API}/listings/${encodeURIComponent(id)}/activity`).then(r => r.data),
  // Phase 60 — dealer reviews
  listDealerReviews: (slug, params) => axios.get(`${API}/dealers/${encodeURIComponent(slug)}/reviews`, { params }).then(r => r.data),
  getMyDealerReview: (slug) => axios.get(`${API}/dealers/${encodeURIComponent(slug)}/reviews/me`).then(r => r.data),
  createDealerReview: (slug, payload) => axios.post(`${API}/dealers/${encodeURIComponent(slug)}/reviews`, payload).then(r => r.data),
  updateDealerReview: (slug, payload) => axios.patch(`${API}/dealers/${encodeURIComponent(slug)}/reviews/me`, payload).then(r => r.data),
  deleteDealerReview: (slug) => axios.delete(`${API}/dealers/${encodeURIComponent(slug)}/reviews/me`).then(r => r.data),
  adminDeleteDealerReview: (id) => axios.delete(`${API}/admin/dealer-reviews/${encodeURIComponent(id)}`).then(r => r.data),
  // dealer (auth, dealer-only)
  dealerMe: () => axios.get(`${API}/dealer/me`).then(r => r.data),
  dealerPatch: (payload) => axios.patch(`${API}/dealer/me`, payload).then(r => r.data),
  dealerCheckSlug: (value) => axios.get(`${API}/dealer/check-slug`, { params: { value } }).then(r => r.data),
  dealerImportCsv: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/dealer/import-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  // admin dealer management
  adminListDealers: (params) => axios.get(`${API}/admin/dealers`, { params }).then(r => r.data),
  adminVerifyDealer: (userId, level = "standard") => axios.post(`${API}/admin/dealers/${userId}/verify`, null, { params: { level } }).then(r => r.data),
  adminUnverifyDealer: (userId) => axios.post(`${API}/admin/dealers/${userId}/unverify`).then(r => r.data),
  // compare history (auth)
  compareHistory: () => axios.get(`${API}/compare/history`).then(r => r.data),
  compareHistoryAdd: (ids, titles) => axios.post(`${API}/compare/history`, { ids, titles }).then(r => r.data),
  compareHistoryDelete: (id) => axios.delete(`${API}/compare/history/${id}`).then(r => r.data),
  // recently viewed (auth, optional cross-device sync)
  recentViews: () => axios.get(`${API}/recent-views`).then(r => r.data),
  recentViewsAdd: (listing_id) => axios.post(`${API}/recent-views`, { listing_id }).then(r => r.data),
  recentViewsDelete: (listing_id) => axios.delete(`${API}/recent-views/${listing_id}`).then(r => r.data),
  // compare view counter (fire-and-forget, no auth)
  // SEO landing pages
  compareSeoPage: (slug, locale = "de") => axios.get(`${API}/compare/seo/${encodeURIComponent(slug)}`, { params: { locale } }).then(r => r.data),
  listCompareSeoPairs: (limit = 24) => axios.get(`${API}/compare/seo`, { params: { limit } }).then(r => r.data),
  compareHub: (locale = "de") => axios.get(`${API}/compare/hub`, { params: { locale } }).then(r => r.data),
  // SEO Brand & Model landing pages (Phase 55.1)
  seoBrand: (brandSlug, locale = "de") => axios.get(`${API}/seo/brand/${encodeURIComponent(brandSlug)}`, { params: { locale } }).then(r => r.data),
  seoBrandModel: (brandSlug, modelSlug, locale = "de") => axios.get(`${API}/seo/brand/${encodeURIComponent(brandSlug)}/${encodeURIComponent(modelSlug)}`, { params: { locale } }).then(r => r.data),
  seoListBrands: (limit = 100) => axios.get(`${API}/seo/brands`, { params: { limit } }).then(r => r.data),
  seoListModelsForBrand: (brandSlug, limit = 200) => axios.get(`${API}/seo/brand/${encodeURIComponent(brandSlug)}/models/sitemap`, { params: { limit } }).then(r => r.data),
  incrementCompareView: (id) => axios.post(`${API}/listings/${id}/compare-view`).then(r => r.data),
  trackCompareEvent: (listing_ids) => axios.post(`${API}/compare/track`, { listing_ids }).then(r => r.data),
  // dashboard stats (auth, owner-scoped)
  dashboardStats: () => axios.get(`${API}/dashboard/stats`).then(r => r.data),
  // web push (auth)
  pushVapidKey: () => axios.get(`${API}/push/vapid-public-key`).then(r => r.data),
  pushSubscribe: (payload) => axios.post(`${API}/push/subscribe`, payload).then(r => r.data),
  pushUnsubscribe: (endpoint) => axios.delete(`${API}/push/subscriptions`, { data: { endpoint } }).then(r => r.data),
  pushMyList: () => axios.get(`${API}/push/subscriptions`).then(r => r.data),
  toggleSearchPush: (id, push_enabled) => axios.patch(`${API}/searches/${id}/push`, { push_enabled }).then(r => r.data),
  // admin push
  boostPerformance: (listingId) => axios.get(`${API}/listings/${listingId}/boost-performance`).then(r => r.data),
  boostBannerTrack: (listingId, data) => axios.post(`${API}/listings/${listingId}/boost-banner/track`, data).then(r => r.data),
  adminBoostBannerStats: (days = 30) => axios.get(`${API}/admin/boost-banner/stats`, { params: { days } }).then(r => r.data),
  adminPushStats: () => axios.get(`${API}/admin/push/stats`).then(r => r.data),
  adminPushCtr: (days = 7) => axios.get(`${API}/admin/push/ctr`, { params: { days } }).then(r => r.data),
  pushTrackClick: (data) => axios.post(`${API}/push/track/click`, data).then(r => r.data),
  pushTrackListingView: (data) => axios.post(`${API}/push/track/listing-view`, data).then(r => r.data),
  adminCompareStats: () => axios.get(`${API}/admin/compare/stats`).then(r => r.data),
  adminPushTest: (body) => axios.post(`${API}/admin/push/test`, body || {}).then(r => r.data),
  adminDispatchSavedSearchesPush: (dry_run = false) => axios.post(`${API}/admin/saved-searches/dispatch-push`, null, { params: { dry_run } }).then(r => r.data),
  // public contact form
  contact: (body) => axios.post(`${API}/contact`, body).then(r => r.data),
  // account settings
  updateProfile: (patch) => axios.patch(`${API}/auth/me`, patch).then(r => r.data),
  changePassword: (current_password, new_password) => axios.post(`${API}/auth/change-password`, { current_password, new_password }).then(r => r.data),
  deleteAccount: (current_password, confirm) => axios.delete(`${API}/auth/account`, { data: { current_password, confirm } }).then(r => r.data),
};

export { API };
